/**
 * Clerk's words, in Bearri's. Passed once, as ClerkProvider's `localization` (src/index.tsx), so the sign-in page,
 * the invite page and the profile modal all speak the same way.
 *
 * Bearri is invite-only: a new person can't make an account from the sign-in page, and Clerk refuses a brand-new
 * Google, Facebook or TikTok login on the server. These errors are what that person sees, so each one says what to
 * do next: open the invite email, or join the waitlist (the sign-in card's cream shelf, right under the message).
 *
 * Clerk looks errors up by their code string, so codes the TypeScript type doesn't list yet
 * (sign_up_mode_restricted, sign_up_restricted_waitlist) still work; they are added through a cast.
 */
import type React from 'react';
import type { ClerkProvider } from '@clerk/clerk-react';

export type ClerkLocalization = NonNullable<React.ComponentProps<typeof ClerkProvider>['localization']>;

export const NO_ACCOUNT_YET =
  'There’s no Bearri account for that login yet. Got an invite? Open the link in your invite email. Otherwise, join the waitlist below.';
// someone invited types their email here before opening the invite: point them at the invite first
export const NO_ACCOUNT_FOR_EMAIL =
  'No Bearri account uses that email yet. Got an invite? Open the link in your invite email. Otherwise, join the waitlist below.';
export const NOT_ALLOWED =
  'That login can’t open Bearri yet. Got an invite? Open the link in your invite email. Otherwise, join the waitlist below.';

export const INVITE_LINK_FAILED_TITLE = 'This invite link doesn’t work';
export const INVITE_LINK_FAILED_SUBTITLE = 'It may have expired or been used. Join the waitlist and we’ll send a fresh one.';

/** Every Clerk error code a person without an account can hit, keyed exactly as Clerk sends it. */
export const BEARRI_CLERK_ERRORS: Record<string, string> = {
  // a new Google / Facebook / TikTok login on the sign-in page (transferable={false} keeps them there)
  external_account_not_found: NO_ACCOUNT_YET,
  // a typed email with no account behind it
  form_identifier_not_found: NO_ACCOUNT_FOR_EMAIL,
  // an allowlist / blocklist refusal
  not_allowed_access: NOT_ALLOWED,
  // Clerk's Access mode set to Restricted, or to Waitlist: any sign-up without an invite
  sign_up_mode_restricted: NO_ACCOUNT_YET,
  sign_up_restricted_waitlist: NO_ACCOUNT_YET,
};

export const BEARRI_CLERK_LOCALIZATION: ClerkLocalization = {
  socialButtonsBlockButton: 'Continue with {{provider|titleize}}',
  // Clerk's default for several stacked providers is the bare provider name
  socialButtonsBlockButtonManyInView: 'Continue with {{provider|titleize}}',
  dividerText: 'or',
  formFieldLabel__emailAddress: 'Email',
  formFieldInputPlaceholder__emailAddress: 'you@email.com',
  signIn: {
    start: {
      title: 'Sign in to Bearri',
      subtitle: 'Welcome back.',
      // the waitlist-mode footer action; hidden on our card (the shelf says it), worded the same in case it shows
      actionText__join_waitlist: 'New here?',
      actionLink__join_waitlist: 'Join the waitlist',
    },
  },
  signUp: {
    start: {
      title: 'Make your Bearri account',
      subtitle: 'Use the email your invite went to.',
      actionText: 'Already made one?',
      actionLink: 'Sign in',
    },
    // the invite page with no invite in the link, or a link that has expired or been used
    restrictedAccess: {
      title: INVITE_LINK_FAILED_TITLE,
      subtitle: INVITE_LINK_FAILED_SUBTITLE,
      subtitleWaitlist: INVITE_LINK_FAILED_SUBTITLE,
      blockButton__joinWaitlist: 'Join the waitlist',
      blockButton__emailSupport: 'Email support',
      actionText: 'Already made one?',
      actionLink: 'Sign in',
    },
  },
  unstable__errors: BEARRI_CLERK_ERRORS as ClerkLocalization['unstable__errors'],
};
